import {
    DeductibleTrackerState,
    FetchState,
    HealthPlan,
} from "@/shared/types";
import * as deductibleTrackerService from "@/shared/api/deductibleTrackerService";
import * as storeHelper from "@/shared/storeHelper";
import { defineStore } from "pinia";
import _ from "lodash";
import { useThemeStore } from "@/shared/store/theme";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";

export const initialState: DeductibleTrackerState = {
    iframeUrlFetchState: FetchState.Uninitialized,
    iframeUrl: null,
    healthPlanIsLinkedFetchState: FetchState.Uninitialized,
    healthPlanIsLinked: false,
    healthPlansFetchState: FetchState.Uninitialized,
    healthPlans: [],
    connectionError: false
};


export const useDeductibleTrackerStore = defineStore("deductibleTracker", {
    state: (): DeductibleTrackerState => (_.cloneDeep(initialState)),
    getters: {
        isIframeLoaded: state => state.iframeUrlFetchState === FetchState.Loaded,
        isHealthPlanIsLinkedLoaded: state => state.healthPlanIsLinkedFetchState === FetchState.Loaded,
        areHealthPlansLoaded: state => state.healthPlansFetchState === FetchState.Loaded,
    },
    actions: {
        setIframeUrlFetchState (newState: FetchState) {
            this.iframeUrlFetchState = newState;
        },
        setIframeUrl (iframeUrl: string): void {
            this.iframeUrl = iframeUrl;
        },
        setHealthPlanIsLinkedFetchState (newState: FetchState) {
            this.healthPlanIsLinkedFetchState = newState;
        },
        setHealthPlanIsLinked (hasHealthPlans: boolean): void {
            this.healthPlanIsLinked = hasHealthPlans;
        },
        setHealthPlansFetchState (newState: FetchState) {
            this.healthPlansFetchState = newState;
        },
        setHealthPlans (healthPlans: HealthPlan[]): void {
            this.healthPlans = healthPlans;
        },
        setConnectionError (value: boolean): void {
            this.connectionError = value;
        },
        retrieveIframeUrl (): Promise<any> {
            this.setConnectionError(false);
            this.setIframeUrlFetchState(FetchState.Loading);
            return deductibleTrackerService.getIframeUrl().then(response => {

                const themeStore = useThemeStore();
                const primaryColorHex = themeStore.primaryColor;
                const iframeUrl = `${response.data}&primaryColor=${primaryColorHex.replace("#", "")}`;

                this.setIframeUrl(iframeUrl);
                this.setIframeUrlFetchState(FetchState.Loaded);
            }).catch(err => {
                if (err && err.response && err.response.status !== 200 && err.response.status !== 404) {
                    this.setConnectionError(true);
                } else {
                    storeHelper.handleError({ error: err, rethrow: false });
                }
            });
        },
        retrieveHealthPlanIsLinked (): Promise<any> {
            const featureFlagStore = useFeatureFlagStore();
            if (featureFlagStore.flags.overrideHealthPlanIsLinked) {
                this.setConnectionError(false);
                this.setHealthPlanIsLinked(true);
                this.setHealthPlanIsLinkedFetchState(FetchState.Loaded);
                return;
            }

            this.setConnectionError(false);
            this.setHealthPlanIsLinkedFetchState(FetchState.Loading);
            return deductibleTrackerService.getHealthPlanIsLinked().then(response => {
                this.setHealthPlanIsLinked(response.data);
                this.setHealthPlanIsLinkedFetchState(FetchState.Loaded);
            }).catch(err => {
                if (err && err.response && err.response.status !== 200 && err.response.status !== 404) {
                    this.setConnectionError(true);
                } else {
                    storeHelper.handleError({ error: err, rethrow: false });
                }
            });
        },
        loadHealthPlans (): Promise<any> {
            this.setConnectionError(false);
            this.setHealthPlansFetchState(FetchState.Loading);
            return deductibleTrackerService.getHealthPlans().then(response => {
                this.setHealthPlans(response.data);
                this.setHealthPlansFetchState(FetchState.Loaded);
            }).catch(err => {
                if (err && err.response && err.response.status !== 200 && err.response.status !== 404) {
                    this.setConnectionError(true);
                } else {
                    storeHelper.handleError({ error: err, rethrow: false });
                }
            });
        }
    }
});
